// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.667Z
// Collection: wipPosts/$postId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Work-in-progress art posts from the artist for the Artist Room feature. Admin and founder manage, public read.
 */

export interface WipPostsRequest {
  title: string;
  description?: string;
  mediaUrl: string;
  mediaType: string;
  order: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WipPostsResponse {
  title: string;
  description?: string;
  mediaUrl: string;
  mediaType: string;
  order: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a WipPosts operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWipPosts(postId: string, data: WipPostsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `wipPosts/${postId}`, document: doc };
}

/**
 * Admin or founder only. Creates a new WIP post with title, media URL, media type (image or video), and display order. Title, mediaUrl, and mediaType are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWipPosts(postId: string, data: WipPostsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`wipPosts/${postId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting WipPosts: ${formatError(error)}`);
    return false;
  }
}

export type WipPostsRequestUpdate = Partial<WipPostsRequest>;

/**
 * Build a WipPosts update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateWipPosts(postId: string, data: WipPostsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `wipPosts/${postId}`, document: doc };
}

/**
 * Admin or founder only. Can update description and order. Title, mediaUrl, and mediaType cannot be changed after creation. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateWipPosts(postId: string, data: WipPostsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`wipPosts/${postId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating WipPosts: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
   (Get Single Item)
 */
export async function getWipPosts(postId: string): Promise<WipPostsResponse | null> {
  try {
    const result = await get(`wipPosts/${postId}`);
    return result as WipPostsResponse | null;
  } catch (error) {
    console.error(`Error getting WipPosts: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single WipPosts document. (
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
  )
 */
export function subscribeWipPosts(callback: (data: WipPostsResponse | null) => void, postId: string): Promise<() => Promise<void>> {
  return subscribe(`wipPosts/${postId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WipPostsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to WipPosts at path '${`wipPosts/${postId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many WipPosts items from collection wipPosts
 
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
  
 */
export async function getManyWipPosts(filter: string = ""): Promise<WipPostsResponse[]> {
  try {
    const results = await get("wipPosts", { prompt: filter });
    return Array.isArray(results) ? results as WipPostsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipPosts collection at path 'wipPosts': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipPosts collection at wipPosts
 
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
  
 */
export function subscribeManyWipPosts(callback: (data: WipPostsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipPosts", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipPostsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipPosts collection at path '${"wipPosts"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all WipPosts items from collection wipPosts
 
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
  
 */
export async function getAllWipPosts(filter: string = ""): Promise<WipPostsResponse[]> {
  try {
    const results = await get("wipPosts", { prompt: filter });
    return Array.isArray(results) ? results as WipPostsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipPosts collection at path 'wipPosts': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipPosts collection at wipPosts
 
  Read Operation Details: Anyone can read WIP posts. Displayed publicly on the Artist Room page to showcase work-in-progress art.
  
 */
export function subscribeAllWipPosts(callback: (data: WipPostsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipPosts", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipPostsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipPosts collection at path '${"wipPosts"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count WipPosts items in collection wipPosts.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countWipPosts(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("wipPosts", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting WipPosts at path 'wipPosts': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on WipPosts items in collection wipPosts.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateWipPosts(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("wipPosts", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating WipPosts at path 'wipPosts': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Removes a WIP post from the gallery.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteWipPosts(postId: string): Promise<boolean> {
  try {
    const succeeded = await set(`wipPosts/${postId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting WipPosts: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for WipPosts for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteWipPosts(postId: string): DocumentOperation {
  return { path: `wipPosts/${postId}`, document: null };
}

